import '../env.d.ts';
import { buildUrl, unwrap } from './utils';
import { openInIframe } from '@shared/iFrameHandler/openInIframe';
import { isTroubleshootingUrl } from '@idConnect/identification/troubleshooting/handleTroubleshootService';
import { idConnectCallbackSuccess } from './actions';
import { isAnyIdAvailable } from '@shared/helpers/idGraph/isAnyIdAvailable';
import { ConnectMessageType, IdConnectDataType } from '@idConnect/types';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { extractParametersFromAddress } from '@idConnect/helpers/extractParametersFromAddress';
import { consoleLogger } from '@shared/logging/consoleLogger';

export const getData: Maker<Ctx, Snap, ConnectMessageType> = ({
  makeEffect,
}) => [
  makeEffect(
    'nonUtiqIDCONNECT_TYPE',
    (_msg, { ctx, pass, skip }) =>
      ctx.window.Utiq!.data!.clientConfig!.IDCONNECT_CONFIG.IDCONNECT_TYPE !==
      'utiq'
        ? pass(
            `Cannot call getDataMessageUtiq due to IDCONNECT_TYPE ${
              ctx.window.Utiq!.data!.clientConfig!.IDCONNECT_CONFIG
                .IDCONNECT_TYPE
            } `,
          )
        : skip(''),
    (_, { ctx }) => {
      ctx.iframeMsgs.shift();
    },
  ),

  makeEffect(
    'statusNotDONE',
    (msg, { pass, skip }) => {
      if (msg.status === 'DONE') return skip("msg.status === 'DONE'");
      if (msg.status === '') return skip("msg.status === ''");
      return pass(msg.status, 'msg.status');
    },
    (status, { ctx }) => {
      ctx.iframeMsgs.shift();
      consoleLogger.logDebug(
        `Message status different than DONE. Status: ${status}`,
      );
      consoleLogger.logDebug('Stopping the calls flow...');
    },
  ),

  makeEffect(
    'bodyMissing',
    (msg, { cond }) => {
      return cond(!msg.body, '!msg.body');
    },
    (_, { ctx }) => {
      ctx.iframeMsgs.shift();
    },
  ),

  makeEffect(
    'consentChangePayload',
    (msg, { ctx, pass, skip }) => {
      if (msg.body.data?.consents?.length === 0)
        return skip('no consents in message');

      if (ctx.consentPayload === null)
        return skip('no consent payload due to no change in consent');
      const data = ctx.consentPayload;

      if ((msg.body.data?.consents?.length ?? 0) > 0)
        return skip('msg.body.data.consents.length > 0');

      const configuredUrl =
        (window.Utiq?.config?.utiqHost ?? '') +
        (window.Utiq?.config?.apiPath ?? '');

      const url = buildUrl(ctx, ['utiqHost'])
        ?.tryPathFrom(configuredUrl)
        ?.withTransactionId()
        .render();

      if (!url) return skip('url unbuildable');

      const active = ctx.axios.active.find(
        ({ req }) =>
          req?.url === url && JSON.stringify(req.data) === JSON.stringify(data),
      );

      if (active) return skip('already in flight', active, data);

      return pass({ url, data }, '');
    },
    ({ url, data }, { ctx }) => {
      ctx.consentPayload = null;
      ctx.axios.instance({
        url,
        data,
        method: 'POST',
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 4000,
      });
    },
  ),

  makeEffect(
    'retainData',
    (msg, { ctx, pass, skip }) => {
      if (
        msg.body.data &&
        !(
          JSON.stringify(msg.body.data) ===
          JSON.stringify(ctx.state.resolvingConnectId)
        )
      ) {
        return pass(msg.body.data);
      }

      return skip('');
    },
    (connectId, { ctx }) => {
      ctx.state.resolvingConnectId = connectId;
    },
  ),

  makeEffect(
    /*
    This differs from the lower effect (`openIframe`) in that it is triggered when `messageConnectId.refresh` is present
    */
    'openIframeAndRefresh',
    (msg, { ctx, snap, pass, skip }) => {
      const { data: messageConnectId } = msg.body;

      if (messageConnectId && messageConnectId.refresh) {
        const iFrameUrl = buildUrl(ctx, ['idConnectConfig', 'customUtiqHost'])
          ?.tryPathFrom(
            ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
              .IDCONNECT_API_HOST,
          )
          ?.addPathSegment('mno-selector')
          .withParam('diagnostic', isTroubleshootingUrl() ? 'true' : undefined)
          .withParam('d', messageConnectId.domain)
          .withParam('category', messageConnectId.idGraph?.[0]?.category)
          .render();

        if (!iFrameUrl) return skip('invalid url');

        const utiqPass = {
          ...snap.localStorage.utiqPass,
          connectId: msg.body.data,
        };

        return pass({ iFrameUrl, utiqPass });
      }

      return skip('');
    },

    ({ iFrameUrl, utiqPass }, { ctx, snap }) => {
      ctx.iframeMsgs.shift();
      ctx.window.localStorage.setItem(
        'utiqDataCallTimestamp',
        snap.now.getTime().toString(),
      );
      ctx.window.localStorage.setItem('utiqPass', JSON.stringify(utiqPass));
      openInIframe({
        iFrameUrl,
        iFrameId: 'MNOSELECTOR',
        environment: 'Utiq',
      });
    },
  ),
  makeEffect(
    'openIframe',
    (msg, { ctx, pass, skip }) => {
      const { data: messageConnectId } = msg.body;

      if (
        messageConnectId &&
        !isAnyIdAvailable(messageConnectId.idGraph ?? [])
      ) {
        const url = buildUrl(ctx, ['idConnectConfig', 'customUtiqHost'])
          ?.tryPathFrom(
            ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
              .IDCONNECT_API_HOST,
          )
          ?.addPathSegment('mno-selector')
          .withParam('diagnostic', isTroubleshootingUrl() ? 'true' : undefined)
          .withParam('d', messageConnectId.domain)
          .withParam('category', messageConnectId.idGraph?.[0]?.category)
          .render();

        if (!url) return skip('url unbuildable');

        return pass(url);
      }

      return skip('');
    },

    (iFrameUrl, { ctx }) => {
      ctx.iframeMsgs.shift();
      openInIframe({
        iFrameUrl,
        iFrameId: 'MNOSELECTOR',
        environment: 'Utiq',
      });
    },
  ),

  // TODO it seems like this never gets triggered, and I can't even see where this logic was in the legacy codebase, is it still needed?
  makeEffect(
    'requestAdditionalConsent',
    (msg, { snap, ctx, skip, pass }) => {
      const { data: connectId } = msg.body;
      if (!connectId) return skip('!connectId');

      const { utiqHost, apiPath } = unwrap(ctx.window.Utiq).config ?? {};
      if (!utiqHost) return skip('!utiqHost');
      if (!apiPath) return skip('!apiPath');

      if ((msg.body.data?.idGraph?.length ?? 0) > 0)
        return skip('(msg.body.data?.idGraph?.length ?? 0) > 0');

      const url = new URL(utiqHost);
      url.pathname = apiPath;
      url.searchParams.set(
        'transactionId',
        unwrap(ctx.window.Utiq?.transactionId),
      );

      return pass({
        url: url.toString(),
        data: {
          [window.location.origin]: {
            consentParsed: 'general:1',
            consentState: 'accepted',
            consentVersion: snap.cookies.utiq_consent_version,
            consentVersionPrevious: snap.cookies.utiq_consent_version_previous,
            name: 'customConsent',
            value: 'utiqConsent',
          },
        },
      });
    },

    ({ url, data }, { ctx }) => {
      ctx.axios.instance({
        url,
        method: 'POST',
        data: JSON.stringify(data),
      });
    },
  ),

  makeEffect(
    'updateIdConnectData',
    (msg, { ctx, skip, pass }) => {
      const { data: connectId } = msg.body;
      if (!connectId) return skip('!connectId');

      if ((msg.body.data?.idGraph?.length ?? 0) === 0)
        return skip('(msg.body.data?.idGraph?.length ?? 0) === 0');

      const apiHost =
        ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
          ?.IDCONNECT_API_HOST;
      if (!apiHost) return skip('!apiHost');

      const apiAddress = apiHost;
      const parameters = extractParametersFromAddress(apiAddress!);
      if (!parameters) return skip('!parameters');
      const { domain } = parameters;
      if (!domain) return skip('!domain');

      const { createdDate, ...connectIdWithoutCreatedDate } = connectId;
      const callbackData: IdConnectDataType = {
        connectId: connectIdWithoutCreatedDate,
        ids: {
          source: {
            connectId: connectId,
            domain,
            umdid: null,
            originalUmdidCookie: null,
          },
          target: {
            connectId: null,
            domain: null,
            umdid: null,
            umdidCookie: null,
          } as any,
        },
      };

      if (Array.isArray(connectId.idGraph)) {
        for (const idGraph of connectId.idGraph) {
          if (typeof idGraph.mtid !== 'undefined') {
            callbackData.connectStatus = 'OK';
          }
        }
      }

      return pass(callbackData);
    },
    (callbackData, { ctx, snap }) => {
      ctx.iframeMsgs.shift();

      idConnectCallbackSuccess(snap, callbackData);
    },
  ),
];
